import { useState, useEffect } from 'react';

import { Tabs } from '@/components/Tabs';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import AccountSecurity from '@/pages/common/settingTabs/AccountSecurity';
import LanguageRegion from '@/pages/common/settingTabs/LanguageRegion';

import DeletedRecords from './components/DeletedRecords';
import ProfileSettings from './components/ProfileSettings';
import SchoolSubscription from './components/SchoolSubscription';

// ─── Types ───────────────────────────────────────────────────────────────────

type TSettingsTab =
  | 'profile'
  | 'language'
  | 'security'
  | 'subscription'
  | 'deleted';

// ─── Tab Options ─────────────────────────────────────────────────────────────

const useSettingsTabs = () => {
  const { formatText } = useFormatText();

  return [
    {
      label: formatText('settings.tabs.profile-settings'),
      value: 'profile' as const,
    },
    {
      label: formatText('settings.tabs.language-region'),
      value: 'language' as const,
    },
    {
      label: formatText('settings.tabs.account-security'),
      value: 'security' as const,
    },
    {
      label: formatText('settings.tabs.subscription'),
      value: 'subscription' as const,
    },
    {
      label: formatText('settings.tabs.deleted-records'),
      value: 'deleted' as const,
    },
  ];
};

// ─── Tab Content Map ─────────────────────────────────────────────────────────

const TAB_CONTENT: Record<TSettingsTab, React.FC> = {
  profile: ProfileSettings,
  language: LanguageRegion,
  security: AccountSecurity,
  subscription: SchoolSubscription,
  deleted: DeletedRecords,
};

// ─── Settings Page ───────────────────────────────────────────────────────────

const Settings = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();

  useEffect(() => {
    setTitle(formatText('settings.page-title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeTab, setActiveTab] = useState<TSettingsTab>('profile');
  const tabOptions = useSettingsTabs();
  const ActiveComponent = TAB_CONTENT[activeTab];

  return (
    <div
      className="flex flex-col gap-6 py-6"
      data-testid="settings-page-container"
    >
      {/* Tabs */}
      <Tabs
        options={tabOptions}
        value={activeTab}
        onChange={setActiveTab}
        dataTestId="settings-tabs"
      />

      {/* Tab Content */}
      <div
        className="mt-2 mx-auto w-full flex items-center justify-center"
        data-testid="settings-tab-content"
      >
        <ActiveComponent />
      </div>
    </div>
  );
};

export default Settings;
